import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Droplets, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Progress } from '../ui/progress';

const qualityParameters = [
  { 
    name: 'pH', 
    range: '6.5 - 8.5', 
    actual: 7.3, 
    unit: '', 
    status: 'good',
    trend: 'stable',
    lastReading: '2 hours ago'
  },
  { 
    name: 'Turbidity', 
    range: '0 - 1', 
    actual: 0.8, 
    unit: 'NTU', 
    status: 'good',
    trend: 'down',
    lastReading: '2 hours ago'
  },
  { 
    name: 'TDS', 
    range: '0 - 500', 
    actual: 430, 
    unit: 'mg/L', 
    status: 'good',
    trend: 'stable',
    lastReading: '2 hours ago'
  },
  { 
    name: 'Chlorine', 
    range: '0.2 - 1.0', 
    actual: 0.4, 
    unit: 'mg/L', 
    status: 'good',
    trend: 'stable',
    lastReading: '2 hours ago'
  },
  { 
    name: 'Fluoride', 
    range: '0 - 1.5', 
    actual: 0.9, 
    unit: 'mg/L', 
    status: 'good',
    trend: 'stable',
    lastReading: '2 hours ago'
  },
];

const trendData = [
  { date: '12 Jan', ph: 7.2, turbidity: 0.9, chlorine: 0.5, tds: 420 },
  { date: '13 Jan', ph: 7.3, turbidity: 0.8, chlorine: 0.4, tds: 425 },
  { date: '14 Jan', ph: 7.1, turbidity: 0.7, chlorine: 0.6, tds: 430 },
  { date: '15 Jan', ph: 7.4, turbidity: 0.8, chlorine: 0.5, tds: 435 },
  { date: '16 Jan', ph: 7.2, turbidity: 0.9, chlorine: 0.4, tds: 428 },
  { date: '17 Jan', ph: 7.3, turbidity: 0.8, chlorine: 0.4, tds: 430 },
];

const samplingLocations = [
  { location: 'WTP-1 Output', samples: 12, compliant: 11, nonCompliant: 1, rate: 91.7 },
  { location: 'OHT-1', samples: 8, compliant: 8, nonCompliant: 0, rate: 100 },
  { location: 'OHT-2', samples: 8, compliant: 7, nonCompliant: 1, rate: 87.5 },
  { location: 'DMA-01 Entry', samples: 6, compliant: 6, nonCompliant: 0, rate: 100 },
  { location: 'DMA-03 Entry', samples: 6, compliant: 5, nonCompliant: 1, rate: 83.3 },
  { location: 'Consumer Tap - Zone A', samples: 10, compliant: 10, nonCompliant: 0, rate: 100 },
  { location: 'Consumer Tap - Zone B', samples: 10, compliant: 9, nonCompliant: 1, rate: 90.0 },
];

const getStatusIcon = (status) => {
  if (status === 'good') return <CheckCircle2 className="w-4 h-4 text-success" />;
  if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-warning" />;
  return <XCircle className="w-4 h-4 text-destructive" />;
};

const getTrendIcon = (trend) => {
  if (trend === 'up') return <TrendingUp className="w-3 h-3 text-destructive" />;
  if (trend === 'down') return <TrendingDown className="w-3 h-3 text-success" />;
  return <div className="w-3 h-3 rounded-full bg-muted-foreground" />;
};

export const QualityMonitoring = ({ selectedDistrict, selectedPanchayat }) => {
  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Water Quality Monitoring</h1>
        <p className="text-muted-foreground">Real-time quality parameters and compliance tracking</p>
      </div>
      
      {/* Quality Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Compliance Rate</p>
                <p className="text-3xl font-bold text-success">96.2%</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-success" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">60 samples tested this week</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Active Alerts</p>
                <p className="text-3xl font-bold text-warning">1</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-warning" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Low chlorine residual</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Sampling Points</p>
                <p className="text-3xl font-bold">42</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-primary" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Across all panchayats</p>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Last Lab Test</p>
                <p className="text-3xl font-bold">2h</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-accent" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Real-time monitoring active</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Quality Parameters Table */}
      <Card>
        <CardHeader>
          <CardTitle>Current Quality Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th className="text-center">Acceptable Range</th>
                  <th className="text-center">Current Value</th>
                  <th className="text-center">Unit</th>
                  <th className="text-center">Trend</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Last Reading</th>
                </tr>
              </thead>
              <tbody>
                {qualityParameters.map((param, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{param.name}</td>
                    <td className="text-center text-muted-foreground">{param.range}</td>
                    <td className="text-center">
                      <span className={`font-semibold ${
                        param.status === 'good' ? 'text-success' :
                        param.status === 'warning' ? 'text-warning' : 'text-destructive'
                      }`}>
                        {param.actual}
                      </span>
                    </td>
                    <td className="text-center text-muted-foreground">{param.unit || '-'}</td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getTrendIcon(param.trend)}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        {getStatusIcon(param.status)}
                        <Badge 
                          variant="outline"
                          className={
                            param.status === 'good' ? 'bg-success/10 text-success border-success/20' :
                            param.status === 'warning' ? 'bg-warning/10 text-warning border-warning/20' :
                            'bg-destructive/10 text-destructive border-destructive/20'
                          }
                        >
                          {param.status === 'good' ? 'Pass' : param.status === 'warning' ? 'Warning' : 'Fail'}
                        </Badge>
                      </div>
                    </td>
                    <td className="text-center text-xs text-muted-foreground">{param.lastReading}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>pH & Turbidity Trend (7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="ph" stroke="hsl(var(--chart-1))" name="pH" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="turbidity" stroke="hsl(var(--chart-2))" name="Turbidity (NTU)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Chlorine & TDS Trend (7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" />
                <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="chlorine" stroke="hsl(var(--chart-3))" name="Chlorine (mg/L)" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="tds" stroke="hsl(var(--chart-4))" name="TDS (mg/L)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      {/* Sampling Locations */}
      <Card>
        <CardHeader>
          <CardTitle>Sampling Location Compliance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {samplingLocations.map((loc, idx) => (
              <div key={idx} className="p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-foreground">{loc.location}</h4>
                    <p className="text-sm text-muted-foreground">
                      {loc.samples} samples | {loc.compliant} compliant | {loc.nonCompliant} non-compliant
                    </p>
                  </div>
                  <Badge 
                    variant="outline"
                    className={
                      loc.rate === 100 ? 'bg-success/10 text-success border-success/20' :
                      loc.rate >= 90 ? 'bg-warning/10 text-warning border-warning/20' :
                      'bg-destructive/10 text-destructive border-destructive/20'
                    }
                  >
                    {loc.rate}%
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Progress 
                    value={loc.rate} 
                    className="flex-1 h-2" 
                    indicatorClassName={
                      loc.rate === 100 ? 'bg-success' :
                      loc.rate >= 90 ? 'bg-warning' : 'bg-destructive'
                    }
                  />
                  <span className="text-sm font-medium">{loc.rate}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Active Quality Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Active Quality Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-4 p-4 bg-warning/5 rounded-lg border border-warning/20">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-warning flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-warning-foreground" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-foreground">Low Chlorine Residual</h4>
                    <p className="text-sm text-muted-foreground">DMA-03 Entry | Value: 0.15 mg/L (Min: 0.2 mg/L)</p>
                  </div>
                  <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">Warning</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Detected: 2 hours ago</span>
                  <span>•</span>
                  <span>Status: Dosing adjusted</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 p-4 bg-success/5 rounded-lg border border-success/20">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-success-foreground" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-foreground">Chlorine Level Stabilized</h4>
                    <p className="text-sm text-muted-foreground">WTP-1 Output | Value: 0.4 mg/L (Range: 0.2-1.0 mg/L)</p>
                  </div>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20">Resolved</Badge>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Resolved: 1 hour ago</span>
                  <span>•</span>
                  <span>Status: Normal operation</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QualityMonitoring;